/**
 * Per-person read receipts for the Notification Center and the shell bell. The record is the `notification_reads`
 * table (one row per person per item, RLS: own rows only), so what one person opens never changes what anyone else
 * sees. Until that migration has run, the browser copy stands in and the page says so.
 */
import { useCallback, useEffect, useState } from 'react';
import { supabaseAdmin } from '@/integrations/supabase/client';

// notification_reads is newer than the generated types; the table shape is in the migration.
const db = supabaseAdmin as any;
import { readSet, saveReadSet, readRows, readIdsFrom, isMissingTable } from '@/lib/notifications/center';

const storage = () => (typeof window !== 'undefined' ? window.localStorage : undefined);
const READS_EVENT = 'lp:notification-reads';

export function useNotificationReads(userId: string | undefined) {
  const viewer = userId || 'anon';
  const [marks, setMarks] = useState<Set<string>>(() => readSet(storage(), viewer));
  const [source, setSource] = useState<'table' | 'browser' | 'loading'>('loading');

  useEffect(() => {
    let cancelled = false;
    setMarks(readSet(storage(), viewer));
    if (!userId) { setSource('browser'); return; }
    db.from('notification_reads').select('item_kind, item_id').eq('user_id', userId).limit(5000)
      .then(({ data, error }: any) => {
        if (cancelled) return;
        if (error) { if (!isMissingTable(error)) console.warn('notification_reads:', error.message); setSource('browser'); return; }
        // Keep marks made in this browser that never reached the table (marked while it was still loading, or a failed
        // write), and send them up, instead of dropping them on reload
        const set = readIdsFrom(data);
        const missing = [...readSet(storage(), viewer)].filter((id) => !set.has(id));
        missing.forEach((id) => set.add(id));
        setMarks(set); saveReadSet(storage(), viewer, set); setSource('table');
        if (missing.length) {
          db.from('notification_reads').upsert(readRows(userId, missing), { onConflict: 'user_id,item_kind,item_id', ignoreDuplicates: true })
            .then(({ error: upErr }: any) => { if (upErr) console.warn('notification_reads catch-up:', upErr.message); });
        }
      });
    return () => { cancelled = true; };
  }, [userId, viewer]);

  // Every hook on the page (the bell and the Notification Center keep their own) takes the same marks at once: "Mark
  // All Read" in the Center used to leave the bell's count up until a reload (Traci's Loom, 28 Sep 2026)
  useEffect(() => {
    const onMarked = (e: Event) => {
      const { viewer: who, ids } = (e as CustomEvent).detail || {};
      if (who !== viewer || !Array.isArray(ids)) return;
      setMarks((prev) => { const next = new Set(prev); ids.forEach((id: string) => next.add(id)); return next; });
    };
    window.addEventListener(READS_EVENT, onMarked);
    return () => window.removeEventListener(READS_EVENT, onMarked);
  }, [viewer]);

  const markRead = useCallback(async (ids: string[]) => {
    const fresh = ids.filter((id) => !marks.has(id));
    if (!fresh.length) return;
    const next = new Set(marks); fresh.forEach((id) => next.add(id));
    setMarks(next); saveReadSet(storage(), viewer, next);
    window.dispatchEvent(new CustomEvent(READS_EVENT, { detail: { viewer, ids: fresh } }));
    if (!userId || source !== 'table') return;
    const { error } = await db.from('notification_reads').upsert(readRows(userId, fresh), { onConflict: 'user_id,item_kind,item_id', ignoreDuplicates: true });
    if (error) console.warn('notification_reads upsert:', error.message);
  }, [marks, userId, viewer, source]);

  return { marks, markRead, source };
}
